import _ from 'lodash';
import { Box, Flex, Loader, Text, Icon } from '@vibe/core';
// @ts-ignore
import { Heading } from '@vibe/core';
import { Board, Person } from '@vibe/icons';
import { ItemPicker } from '../shared/components/ItemPicker';
import { useItemCommentsManager } from '../hooks/use-item-comments-manager';
import { extractErrorMessage } from '../lib/utils';
import { PortalPicker } from './PortalPicker';
import { Comments } from './Comments';
import ErrorBox from './ErrorBox';

function ItemCommentsManager() {
  const {
    addUpdateTrigger,
    addReplyTrigger,
    comments,
    boardId,
    itemId,
    pageSize,
    page,
    setPage,
    currentUser,
    itemCommentsManagerState,
    selectedClientItemId,
    setSelectedClientItemId,
    selectedClientItem,
    currentItem,
    portalsWithBoardInfo,
    selectedPortal,
    selectedPortalId,
    setSelectedPortalId,
    commentsError,
    isLoadingComments,
    arePortalsLoading,
  } = useItemCommentsManager();

  if (itemCommentsManagerState.state === 'error') {
    return <ErrorBox headline="Error while loading item comments manager" description={itemCommentsManagerState.error} />;
  }

  if (itemCommentsManagerState.state === 'init') {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  }

  let content = null;

  if (commentsError) {
    content = <ErrorBox headline="Error while loading comments" description={extractErrorMessage(commentsError)} heightOffset="73px" />;
  } else if (comments && selectedClientItemId && selectedClientItem && currentItem && itemId && currentUser) {
    content = (
      <Comments
        boardId={boardId!}
        itemId={itemId}
        onAddComment={async (comment, files) => {
          const activityMessage = `New comment on "${currentItem.name}" from "${currentUser.name}"`;

          await addUpdateTrigger({
            itemId: itemId,
            clientItemId: selectedClientItemId,
            type: 'to',
            body: comment,
            files: files,
            activityMessage: activityMessage,
            portalId: selectedPortalId!,
          });
        }}
        onAddReply={async (commentId, reply) => {
          const activityMessage = `New reply on "${currentItem.name}" from "${currentUser.name}"`;

          await addReplyTrigger({
            itemId: itemId,
            clientItemId: selectedClientItemId,
            parentId: commentId,
            body: reply,
            type: 'to',
            activityMessage: activityMessage,
            portalId: selectedPortalId!,
          });
        }}
        comments={comments}
        page={page}
        onPageChange={(page) => {
          setPage(page);
        }}
        commentsPerPage={pageSize}
      />
    );
  } else if (!comments && isLoadingComments) {
    content = (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: 'calc(100vh - 73px)',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  } else if (arePortalsLoading) {
    content = (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: 'calc(100vh - 73px)',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  } else {
    content = (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '24px',
          width: 'calc(100% - 50px)',
          height: 'calc(100vh - 123px)',
          minHeight: '300px',
          border: !selectedPortalId || !selectedClientItemId! ? '1px dashed #D0D4E4' : 'none',
          borderRadius: '4px',
        }}
      >
        {selectedPortalId && selectedClientItemId ? <Loader color="primary" size={30} /> : null}
        <Flex
          direction="column"
          gap={16}
          style={{
            marginTop: '-32px',
          }}
        >
          <Flex direction="column" gap={4}>
            {!selectedPortalId ? <Icon icon={Board} size={32} /> : !selectedClientItemId && <Icon icon={Person} size={32} />}
            <Heading type="h3" weight="bold" align="center">
              {!selectedPortalId ? 'Choose portal' : !selectedClientItemId && 'Choose client'}
            </Heading>
          </Flex>
          <Text type="text2" align="center" color="secondary">
            {!selectedPortalId ? (
              <>
                Please choose a portal from the <br />
                dropdown above to proceed.
              </>
            ) : !selectedClientItemId ? (
              <>
                Please choose a client from the <br />
                dropdown above to proceed.
              </>
            ) : null}
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="portalmanager">
        <Box
          shadow="xs"
          className="portalmanager__header"
          style={{
            padding: '0 24px',
            width: 'calc(100% - 48px)',
          }}
        >
          <Flex
            justify="start"
            align="center"
            direction="row"
            gap={16}
            style={{
              width: '100%',
              height: 'inherit',
            }}
          >
            <Box style={{ flex: 1 }}>
              <PortalPicker
                portals={portalsWithBoardInfo}
                value={selectedPortalId}
                isLoading={arePortalsLoading}
                onChange={(portalId) => {
                  setSelectedPortalId(portalId);
                }}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <ItemPicker
                value={selectedClientItemId}
                onChange={(newClientItemId) => {
                  setSelectedClientItemId(newClientItemId);
                }}
                boardId={+(selectedPortal?.boardId ?? 0)}
                placeholder="Choose a client"
              />
            </Box>
            {/* 
            {showScrollToTopButton && (
              <Box>
                <IconButton
                  icon={Forum}
                  kind="primary"
                  size="medium"
                  onClick={() => {
                    window.scrollTo({ top: 0 });
                  }}
                />
              </Box>
            )}
            */}
          </Flex>
        </Box>

        <Box
          className="portalmanager__content"
          backgroundColor="greyBackgroundColor"
          style={{
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 72px)',
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'inherit',
            }}
          >
            {content}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ItemCommentsManager;
